import * as React from 'react';
import {
    useRecordContext,
    useTranslate,
    TextField,
    NumberField,
    Loading,
    List,
    SimpleShowLayout, NumberInput, required, maxValue, minValue, useRefresh
} from 'react-admin';
import OrderReturnItemShow from './OrderReturnItemShow';
import {Tooltip, Typography} from '@mui/material';
import {EditableDatagrid, EditRowButton, RowForm} from "@react-admin/ra-editable-datagrid";
import {OrderReturn, OrderReturnItem} from '../../types';
import {FunctionField} from "ra-ui-materialui";
import {ConsiderShippingReturnMessage} from "./ConsiderShippingReturnMessage";
import CreateShippingCostDialog from "./dialog/CreateShippingCostDialog";

const QuantityDriveItemEditForm = () => {
    const refresh = useRefresh();

    return (
        <RowForm
            mutationOptions={{
                onSuccess: () => {
                    refresh();
                }
            }}
        >
            <TextField source="sku" label="resources.order.return.items.sku" sortable={false}/>
            <TextField source="productName" label="resources.order.return.items.productName" sortable={false}/>
            <TextField source="reasonDefaultDescription" label="resources.order.return.items.reasonDefaultDescription"
                       sortable={false}/>
            <NumberField
                sortable={false}
                source="confirmedPriceToReturn"
                label="resources.order.return.items.confirmedPriceToReturn"
                options={{style: 'currency', currency: 'EUR'}}
            />

            <FunctionField
                label="resources.order.return.items.nonComplianceReason.headline"
                render={(item: OrderReturnItem) => {
                    let max: number = item.confirmedQuantity < item.requestedQuantity ? item.confirmedQuantity : item.requestedQuantity;
                    return (
                        <NumberInput
                            source="confirmedQuantity"
                            key="confirmedQuantity"
                            validate={[required(), maxValue(max - 1), minValue(0)]}
                            label={`resources.order.return.items.confirmedQuantity`}
                        />
                    );
                }}
            />
        </RowForm>
    );
};

const OrderReturnItems = () => {
    const record = useRecordContext<OrderReturn>();
    const translate = useTranslate();

    if (!record) {
        return <Loading/>;
    }

    let editButton = <div/>;

    if (record.isAccepted && (record.status == 'open' || record.status == 'package_received' || record.status == 'package_lost')) {
        editButton = record.source == 'ONLINE_SHOP' ? <EditRowButton/> : <div/>;
    }

    const editForm = record.source == 'ONLINE_SHOP' ? <QuantityDriveItemEditForm/> : <div/>;
    const shippingCostReturnAvailable = (!record.order?.freeShipping || record.order?.shippingTotalCost > 0) && !record.shippingReturn;

    return (
        <SimpleShowLayout record={record} sx={{width: "100%", marginTop: 2}}>
            <Typography variant="h6" gutterBottom>
                {translate('resources.order.return.itemsHeadline')}
            </Typography>

            <List
                resource="order-return-items"
                filter={{orderReturn: record.id}}
                pagination={false}
                sort={{field: 'id', order: 'ASC'}}
                perPage={100}
                disableSyncWithLocation
                component="div"
                sx={{width: '100%', '& .RaList-actions': {display: shippingCostReturnAvailable ? 'flex' : 'none'}}}
                hasCreate={false}
                exporter={false}
                actions={shippingCostReturnAvailable ? <CreateShippingCostDialog returnId={record.id}/> : false}
            >
                <EditableDatagrid
                    actions={editButton}
                    bulkActionButtons={false}
                    rowClick="expand"
                    expand={<OrderReturnItemShow/>}
                    expandSingle
                    editForm={editForm}
                    sx={{width: '100%'}}
                    mutationMode="pessimistic"
                >
                    <TextField source="sku" label="resources.order.return.items.sku" sortable={false}/>
                    <TextField source="productName" label="resources.order.return.items.productName" sortable={false}/>
                    <TextField source="reasonDefaultDescription"
                               label="resources.order.return.items.reasonDefaultDescription" sortable={false}/>
                    <NumberField
                        sortable={false}
                        source="confirmedPriceToReturn"
                        label="resources.order.return.items.confirmedPriceToReturn"
                        options={{style: 'currency', currency: 'EUR'}}
                    />
                    <NumberField source="confirmedQuantity" label="resources.order.return.items.confirmedQuantity"
                                 sortable={false}/>
                </EditableDatagrid>
            </List>
            {
                record.relatedRequestsCount > 1 && shippingCostReturnAvailable ? <ConsiderShippingReturnMessage/> : null
            }
        </SimpleShowLayout>
    );
};

export default OrderReturnItems;
